// Features: M004-F0715, M004-F0720, M004-F0736 (WebUI #167: the amount edit moved into the item drawer)
// Tests: T-M004-0210, T-M004-0225
//
// The visible parts of the customer page's moves (design M004-F0712-F0716 sections 4.1-4.7a, 4.6). Everything here is
// driven by the `moves` controller from useLedgerMoves; LedgerQueueSections only mounts these (so the logic of the lane
// lives in new files and the shared file keeps a minimal set of hooks).
import React, { useState } from 'react';
import { displayTxId } from '../../utils/transactionId';
import { hasCondition } from '../../services/ledgerMovesAdapter';
import { useTxHistory } from '../txHistoryContext';
import { isTxCreditNoteBackendAvailable } from '../../utils/txFeatureFlags';

// Same amount rule as LedgerQueueSections.parsePounds (section 2.44.6): finite, >= 0, at most 2 decimal places.
// (A copy: importing it from LedgerQueueSections would make the shared file and this one import each other.)
export function parseAmount(value) {
  const text = String(value ?? '').replace(/[£,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const amount = Number(text);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}
const reasonProblem = (reason) => {
  if (!reason) return 'A reason is required.';
  if (reason.length > 500) return 'The reason must be 500 characters or fewer.';
  return '';
};

// Core's / the Ledger's sentences are printed VERBATIM (ACCT-TX-011).
function ConditionLines({ conditions }) {
  return conditions.map((condition) => (
    <span className="ledger-queue-muted" data-testid="ledger-move-condition" data-code={condition.code} key={condition.code}>
      {[condition.message, condition.nextAction].filter(Boolean).join(' ')}
    </span>
  ));
}

// A status line in the section where the action happened. Success, a repeat and a condition are role="status": only a
// refusal the user must act on is an alert (design 4.3 outcome copy; ACCT-TX-017: an advisory is never an error).
export function MoveStatus({ moves, scope }) {
  const notice = moves.notice;
  if (!notice || notice.scope !== scope) return null;
  if (notice.tone === 'error') return <p className="ledger-account-error" role="alert" data-testid="ledger-move-error">{notice.text}</p>;
  return <p className="ledger-queue-muted" role="status" data-testid="ledger-move-status" data-tone={notice.tone}>
    {notice.text}{notice.conditions ? <ConditionLines conditions={notice.conditions} /> : null}
  </p>;
}

// ---------------------------------------------------------------- Advice item: the buttons
export function AdviceMoveActions({ item, moves, disabled = false, canBillByHand = true }) {
  if (!moves.canMove(item)) return null;
  const off = disabled || moves.busy;
  const moving = moves.busyId === item.handOffId;
  return <>
    <button type="button" className="ledger-queue-link" data-testid="ledger-advice-move" disabled={off} onClick={() => moves.move(item, 'draft')}>{moving ? 'Moving…' : 'Move to invoice'}</button>
    {canBillByHand ? <button type="button" className="ledger-queue-link" data-testid="ledger-advice-bill-by-hand" disabled={off}
      onClick={() => moves.openPanel({ kind: 'hand', id: item.handOffId })}>Bill by hand</button> : null}
  </>;
}

// ---------------------------------------------------------------- Advice item: the inline forms
function ReasonForm({ item, moves, panel }) {
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState('');
  const duplicate = hasCondition(panel.conditions, 'duplicate_booking');
  const submit = (event) => {
    event.preventDefault();
    const text = reason.trim();
    const bad = reasonProblem(text);
    if (bad) { setProblem(bad); return; }
    setProblem('');
    // A NEW intent: the body now carries the reason, so it gets its own Idempotency-Key (design 4.2).
    moves.move(item, panel.to, text);
  };
  return <form className="ledger-queue-form" data-testid="ledger-move-reason-form" onSubmit={submit} noValidate>
    <ConditionLines conditions={panel.conditions} />
    <label>{duplicate ? 'Why are you moving this anyway?' : 'Why are you moving this now?'}
      <input required maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
    {problem ? <span className="ledger-account-error" role="alert">{problem}</span> : null}
    <button type="submit" className="ledger-queue-primary" disabled={moves.busy}>{moves.busyId === item.handOffId ? 'Moving…' : (duplicate ? 'Move anyway with this reason' : 'Move with this reason')}</button>
    <button type="button" onClick={moves.closePanel}>Cancel</button>
  </form>;
}

export const CREDIT_INSTEAD_TEXT = 'To bill less, raise a credit note instead.';

// `correction` (design 4.6a, ACCT-TX-016): "Bill more under TX-xxxxxxxx" on a WRITTEN TX. Upward only (amount > 0, never negative), sent
// with `correction: true`; the link under the amount opens the transaction's history drawer on its "Raise credit note" action (W9).
export function BillByHandForm({ item, moves, customerName, correction = false }) {
  const history = useTxHistory();
  const [description, setDescription] = useState(item.description || '');
  const [amount, setAmount] = useState(!correction && Number.isFinite(item.amount) ? item.amount.toFixed(2) : '');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState('');
  const txId = displayTxId({ txnId: item.txId, handOffId: item.handOffId });
  const title = correction ? `Bill more under ${txId || 'this transaction'}` : (txId ? `Bill ${txId} by hand` : 'Bill by hand');
  const submit = (event) => {
    event.preventDefault();
    const text = description.trim();
    const pounds = parseAmount(amount);
    const why = reason.trim();
    if (!text) { setProblem('Enter a description for the line.'); return; }
    if (pounds == null) { setProblem('Enter an amount in pounds, for example 45.00.'); return; }
    if (correction && pounds <= 0) { setProblem(`Enter an amount above zero. ${CREDIT_INSTEAD_TEXT}`); return; }
    const bad = reasonProblem(why);
    if (bad) { setProblem(bad); return; }
    setProblem('');
    moves.billByHand(item, { description: text, amount: pounds, reason: why, correction });
  };
  const openCredit = () => history.openTransaction({ key: item.handOffId, handOffId: item.handOffId, orgId: moves.orgId, customerUserId: moves.userId, action: 'credit' });
  return <form className="ledger-queue-form" data-testid="ledger-bill-by-hand-form" onSubmit={submit} noValidate aria-label={title}>
    <strong>{title}</strong>
    <label>Description<input value={description} onChange={(event) => setDescription(event.target.value)} /></label>
    <label>Amount (£)<input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
    {correction ? (isTxCreditNoteBackendAvailable() && history && item.handOffId && moves.orgId != null
      ? <button type="button" className="ledger-queue-link" data-testid="ledger-correction-credit-link" onClick={openCredit}>{CREDIT_INSTEAD_TEXT}</button>
      : <span className="ledger-queue-muted" data-testid="ledger-correction-credit-text">{CREDIT_INSTEAD_TEXT}</span>) : null}
    <label>Reason (required)<input required maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="e.g. Core could not deliver this cost" /></label>
    <span className="ledger-queue-muted">{correction
      ? `This is billed as a separate correction line on ${customerName ? `${customerName}’s` : 'the customer’s'} next invoice. The original invoiced line stays as it is.`
      : `This puts a line on ${customerName ? `${customerName}’s` : 'the customer’s'} next invoice for this transaction. If Core delivers the same cost later, it joins this line and is never billed twice.`}</span>
    {problem ? <span className="ledger-account-error" role="alert">{problem}</span> : null}
    <button type="submit" className="ledger-queue-primary" disabled={moves.busy}>{correction ? 'Add correction' : 'Add to next invoice'}</button>
    <button type="button" onClick={moves.closePanel}>Cancel</button>
  </form>;
}

export function AdviceMoveForms({ item, moves, customerName }) {
  const panel = moves.panel;
  if (!moves.canMove(item) || !panel || panel.id !== item.handOffId) return null;
  if (panel.kind === 'reason') return <ReasonForm item={item} moves={moves} panel={panel} />;
  if (panel.kind === 'hand') return <BillByHandForm item={item} moves={moves} customerName={customerName} />;
  return null;
}

// ---------------------------------------------------------------- Problems row: Bill by hand (the occ-3252 door)
export function ProblemBillByHand({ row, moves, disabled = false }) {
  if (!moves.canMove(row)) return null;
  return <button type="button" className="ledger-queue-link" data-testid="ledger-problem-bill-by-hand" disabled={disabled || moves.busy}
    onClick={() => moves.openPanel({ kind: 'hand', id: row.handOffId })}>Bill by hand</button>;
}
export function ProblemBillByHandForm({ row, moves, customerName }) {
  const panel = moves.panel;
  if (!moves.canMove(row) || !panel || panel.kind !== 'hand' || panel.id !== row.handOffId) return null;
  return <BillByHandForm item={row} moves={moves} customerName={customerName} />;
}

// ---------------------------------------------------------------- Draft line: Back to advice
export function DraftLineBackToAdvice({ line, moves, disabled = false }) {
  if (!moves.canMove(line)) return null;
  const moving = moves.busyId === line.handOffId;
  return <button type="button" className="ledger-queue-link" data-testid="ledger-draft-back-to-advice" disabled={disabled || moves.busy}
    onClick={() => moves.move(line, 'advice')}>{moving ? 'Moving…' : 'Back to advice'}</button>;
}
